import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	BusyIndicator,
	Form,
	FormItem,
	FormattedText,
	TimeZonePicker,
	ValidationError,
	formatTimeZone
} from "client/Components";
import image from "Content/Images/timezone-step.svg";
import { setDetectedTimeZone } from "client/Api/TimeZones/userTimeZone";
import { TimeZone } from "client/DateAndTime/timezones";
import { ensureNotNil } from "client/Misc/utils";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { PathRedirect } from "client/Validation/serverErrors";
import { ContentLayout } from "./ContentLayout";

export type TimezoneStepProps = {
	detectedTimezone: TimeZone;
	isAnotherTimeZoneAlreadyPersisted: boolean;
	onNext: () => void;
};

export const TimezoneStep = (props: TimezoneStepProps): ReactElement => {
	const {
		detectedTimezone,
		isAnotherTimeZoneAlreadyPersisted,
		onNext: onNextFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({
		timeZoneToPersist: null
	});

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const onNext: () => void = useStableImperativeCallback(onNextFromProps);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await setDetectedTimeZone(
					{
						detectedTimeZone: detectedTimezone.identifier,
						timeZoneToPersist: {
							isSet: true,
							value: ensureNotNil(formData.timeZoneToPersist)
						}
					},
					globalCancellation
				);

				setIsBusy(false);
				onNext();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e, serverPathRedirects)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			detectedTimezone,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onNext
		]
	);

	const cancelHandler = (): void => {
		if (isBusy) {
			return;
		}

		setDetectedTimeZone(
			{
				detectedTimeZone: detectedTimezone.identifier,
				timeZoneToPersist: { isSet: false }
			}
		)
			.catch(e => console.error(e));

		onNext();
	};

	const messageLocalizationKey: LocalizationKey = isAnotherTimeZoneAlreadyPersisted
		? "_WeDetectedThatYourSystemTimeZoneIsDifferentFromProfileSettingsHoweverYourSystemTimeZoneIsNotSupported"
		: "_YourSystemTimeZoneIsNotSupportedInTheWorkspace";

	return (
		<WelcomeStep>
			<WelcomeStep.Left
				trackingId="timezone-step"
				isVerticallyCentred
			>
				{isBusy && (
					<BusyIndicator />
				)}

				<ContentLayout
					title={localize("_ChooseTimeZone")}
					explanation={(
						<FormattedText
							text={localize(messageLocalizationKey)}
							thingsToReplace={[
								{
									what: "{{TimeZoneName}}",
									filler: formatTimeZone(detectedTimezone)
								}
							]}
						/>
					)}
				>
					<Form onSubmit={saveHandler}>
						<FormItem label={localize("_TimeZone")}>
							<TimeZonePicker
								{...formHandler.for("timeZoneToPersist").attachToSelect()}
								placeholder={localize("_SelectTimeZone")}
								menuTitle={localize("_TimeZone")}
							/>

							<ValidationError {...formHandler.for("timeZoneToPersist").attachToValidationError()} />
						</FormItem>
					</Form>

					<ContentLayout.Buttons
						onContinue={saveHandler}
						primaryButtonText={localize("_Save")}
						onSkip={cancelHandler}
					/>
				</ContentLayout>
			</WelcomeStep.Left>

			<WelcomeStep.Right
				imageUrl={image}
			/>
		</WelcomeStep>
	);
};
TimezoneStep.displayName = "TimezoneStep";

const validationConfiguration: IValidationConfiguration<FormData> = createValidationConfiguration<FormData>();
validationConfiguration.forScalar("timeZoneToPersist")
	.add(
		isNotNullNorUndefined()
	);

const serverPathRedirects: readonly PathRedirect[] = [
	{
		pattern: new DotPathWildcardPattern("TimeZoneToPersist.*?"),
		replacement: "TimeZoneToPersist"
	},
	{
		pattern: "DetectedTimeZone",
		replacement: ""
	}
];

type FormData = {
	timeZoneToPersist: string | null;
};